import { apiClient } from '@devd-client/api';
import { parseLLMResponseData } from '../helpers/home.utils';
import { getToken, getOrgId } from '@devd-client/devd/auth';
import {
  RepoListResponse,
  ApiResponse,
  PageDetailsDto,
} from '@devd-client/devd/components';
import {
  CustomDashboardsListResponse,
  CustomDashboardDetailsResponse,
  DimensionResponse,
  MeasureResponse,
  GroupByResponse,
  CustomFieldResponse,
  LLMResponse,
  ContributorInsightsRequest,
} from '../helpers/home.types';

type AiAdoptionPageResponse = ApiResponse<PageDetailsDto>;

export const getContributorInsights = async (
  dataFromSummaryItem: ContributorInsightsRequest
): Promise<LLMResponse> => {
  const requestBody = JSON.stringify(dataFromSummaryItem);
  const currentParams = new URLSearchParams(window.location.search);
  const params = new URLSearchParams({
    start_date: currentParams.get('start') || '',
    end_date: currentParams.get('end') || '',
    team: currentParams.get('team') || '',
  });
  try {
    const res = await fetch(
      `https://api.devdynamics.ai/app/gpt/metric/tooltip?` + params,
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          authorization: `bearer ${getToken()}`,
        },
        body: requestBody,
      }
    );
    const data = await res.text();
    return parseLLMResponseData(data);
  } catch (error) {
    throw new Error('Error in fetching insights.');
  }
};
export const getAllRepos = (source: string): Promise<RepoListResponse> =>
  apiClient(
    `/v1/integration/${source}/org/${getOrgId()}/projects/lite?active=true`,
    {
      method: 'GET',
      headers: {
        authorization: `bearer ${getToken()}`,
      },
    }
  );

export const getAllCustomDashboards =
  (): Promise<CustomDashboardsListResponse> =>
    apiClient(`/v1/custom-dashboard/${getOrgId()}`, {
      method: 'GET',
      headers: {
        authorization: `bearer ${getToken()}`,
      },
    });

export const getCustomDashboardDetails = (
  dashboard: string
): Promise<CustomDashboardDetailsResponse> =>
  apiClient(`/v1/custom-dashboard/${getOrgId()}/${dashboard}`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const createCustomDashboard = (params: any): Promise<any> =>
  apiClient(`/v1/custom-dashboard/${getOrgId()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify(params),
  });

export const renameCustomDashboard = (params: any): Promise<any> =>
  apiClient(`/v1/custom-dashboard/${getOrgId()}/rename`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify(params),
  });

export const deleteCustomDashboard = (params: any): Promise<any> =>
  apiClient(`/v1/custom-dashboard/${getOrgId()}/${params.dashboard}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
  });

export const updateCustomDashboard = (params: any): Promise<any> =>
  apiClient(`/v1/custom-dashboard/${getOrgId()}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify(params),
  });

export const getDimension = (entity: string): Promise<DimensionResponse> =>
  apiClient(`/v1/metrics-metadata?type=dimension&entity=${entity}`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const getMeasure = (): Promise<MeasureResponse> =>
  apiClient(`/v1/metrics-metadata?type=measure`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const getGroupBy = (entity: string): Promise<GroupByResponse> =>
  apiClient(`/v1/metrics-metadata?type=groupby&entity=${entity}`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const getCustomField = (): Promise<CustomFieldResponse> =>
  apiClient(`/v1/integration/org/${getOrgId()}/customFields`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
  });

export const getG2AccessToken = (
  email: string,
  orgId: string
): Promise<ApiResponse<null>> =>
  apiClient(`/v1/user/org/${orgId}?email=${email}`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const g2UserReview = (params: any): Promise<any> =>
  apiClient(`/v1/user/org/${params.orgId}`, {
    method: 'PUT',
    headers: {
      authorization: `bearer ${getToken()}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      id: params.userId,
      orgCode: params.orgId,
      emailId: params.email,
    }),
  });

export const shownTrialEndModal = (params: any): Promise<any> =>
  apiClient(`/v1/user/${params.id}/preference/displayUpgrade`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      authorization: `bearer ${getToken()}`,
    },
    body: JSON.stringify({
      displayed: params.displayed,
    }),
  });

export const getAiAdoptionPage = (): Promise<AiAdoptionPageResponse> =>
  apiClient(`/v2/account/org/${getOrgId()}/page/AIImpact`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });

export const getCopilotAiAdoptionPage = (): Promise<AiAdoptionPageResponse> =>
  apiClient(`/v2/account/org/${getOrgId()}/page/AIAdoption`, {
    method: 'GET',
    headers: {
      authorization: `bearer ${getToken()}`,
    },
  });
